import { Availability, changeAvailability, ChangeAvailabilityResponseStatus } from '@infuse/api/cpms/operator';
import { UiNotificationType } from '@infuse/ui/domain';
import { useNotify } from '@infuse/ui/services';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import axios from 'axios';
import { useI18n } from 'vue-i18n';
import { useOcppCommandMutationUtils } from './utils/ocpp-command-mutation-utils';

export const useChangeAvailability = () => {
  const { waitForOcppResults, isWaitingForResults, apiCorrelationToken } = useOcppCommandMutationUtils();
  useQueryClient();
  const notify = useNotify();
  const { t } = useI18n({ useScope: 'global' });

  const mutation = useMutation<
    unknown,
    unknown,
    {
      chargingStationId: string;
      evseId: number;
      availability: Availability;
      connectorId?: number;
    }
  >({
    mutationFn: async (options) => {
      const { evseId, connectorId, chargingStationId, availability } = options;
      const forStation = evseId === 0;
      const translationKey = availability === Availability.OPERATIVE ? 'setOperative' : 'setInoperative';

      try {
        const result = await changeAvailability({ chargingStationId }, { evseId, connectorId, availability });
        apiCorrelationToken.value = result.apiCorrelationToken;

        // Wait for OCPP command results with status handling
        await waitForOcppResults({
          statusHandlers: [
            {
              responseStatus: ChangeAvailabilityResponseStatus.REJECTED,
              notificationType: UiNotificationType.ERROR,
              translationKey: 'chargingStation.action.changeAvailability.response.rejected'
            },
            {
              responseStatus: ChangeAvailabilityResponseStatus.SCHEDULED,
              notificationType: UiNotificationType.WARNING,
              translationKey: 'chargingStation.action.changeAvailability.response.scheduled'
            }
          ]
        });

        notifySuccess(
          translationKey,
          {
            component: t(`action.${translationKey}.${forStation ? 'chargingStation' : 'connector'}`),
            componentId: forStation ? chargingStationId : `${evseId}-${connectorId}`
          },
          chargingStationId
        );
      } catch (e) {
        if (axios.isAxiosError(e)) {
          notifyError(
            translationKey,
            e.response?.status,
            {
              component: t(`chargingStation.action.${translationKey}.${forStation ? 'chargingStation' : 'connector'}`),
              componentId: forStation ? chargingStationId : `${evseId}-${connectorId}`
            },
            chargingStationId
          );
        }
        throw e;
      }
    },
    onSettled: async () => {
      // TODO: When all operator API are migrated to tanstack invalidate
      // await queryClient.invalidateQueries({ queryKey: ['charging-station'] });
    }
  });

  const notifySuccess = (key: string, msgProps = {}, chargingStationId: string) => {
    notify({
      type: UiNotificationType.SUCCESS,
      group: 'notify-top',
      title: t(`chargingStation.action.${key}.response.success.title`),
      message: t(`chargingStation.action.${key}.response.success.message`, { ...msgProps, csId: chargingStationId }),
      timeout: 5000
    });
  };

  const notifyError = (key: string, code = 500, msgProps = {}, chargingStationId: string) => {
    const tCode = [400, 404, 500].includes(code) ? code : 500;

    notify({
      type: UiNotificationType.ERROR,
      group: 'notify-top',
      title: t(`chargingStation.action.${key}.response.error.${tCode}.title`),
      message: t(`chargingStation.action.${key}.response.error.${tCode}.message`, {
        ...msgProps,
        csId: chargingStationId
      }),
      timeout: 5000
    });
  };

  return {
    ...mutation,
    isWaitingForResults
  };
};
